import router from '@/router';
import {
    graphRequest,
    interactiveLoginRequest,
    loginRequest,
    msalInstance,
    platformRequest
} from '@/util/authConfig';
import { callMsGraphPhoto } from '@/util/msGraphApiCall';
import { decodeJwt, getRolesFromClaims, type AccessTokenClaims } from '@/util/jwt';
import {
    effectiveRoles,
    ladderNameFor,
    maxRank,
    roleRank,
    satisfies,
    ROLE_ADMIN,
    ROLE_STAFF_USER
} from '@/util/roles';
import {
    AUTH_MESSAGES,
    AUTH_NOTIFICATION_KEY,
    PLATFORM_NOTIFICATION_KEY,
    isInteractionRequired,
    isUserCancellation
} from '@/util/authErrors';
import { logAuthFailure, logAuthInfo, logAuthWarning } from '@/util/logger';
import { useNotificationsStore } from '@/stores/notifications.store';
import { ROUTE_NAMES } from '@/lib/enums';
import { EventType, type AccountInfo, type AuthenticationResult } from '@azure/msal-browser';
import { defineStore, getActivePinia, type StoreGeneric } from 'pinia';

interface UserStoreState {
    user: AccountInfo | null;
    accessToken: string | null;
    accessTokenExpiresOn: number | null;
    accessTokenClaims: AccessTokenClaims | null;
    roles: string[];
    /**
     * The 125 platform token and the roles it carries, held separately from the
     * depot trio above because they come from a different resource. Entra will
     * not issue one token for both, so there is no version of this where they
     * share a field.
     */
    platformToken: string | null;
    platformTokenExpiresOn: number | null;
    platformTokenClaims: AccessTokenClaims | null;
    platformRoles: string[];
    /**
     * Role preview override: an admin acting as a lower role to check the UI.
     * Holds a role name, never a rank, so the floor in `effectiveRoles` is the
     * only thing that decides what it grants (which is: never anything).
     */
    roleOverride: string | null;
    allData: any;
    avatarUrl: string | null;
    /** Guards against concurrent/duplicate interactive re-auth attempts. */
    reauthInProgress: boolean;
}

const EXPIRY_SKEW_MS = 60_000;
const SILENT_TOKEN_TIMEOUT_MS = 15_000;

/**
 * Shared in-flight silent refresh. Every caller (the router guard plus every API
 * call) joins the same MSAL request instead of firing a parallel one, and the
 * timeout race means a silent refresh that never settles can't pin them all
 * forever. ponytail: one slot is enough, there is only ever one token to renew.
 */
let silentRefresh: Promise<AuthenticationResult> | null = null;

const acquireTokenSilentOnce = (account: AccountInfo): Promise<AuthenticationResult> => {
    if (!silentRefresh) {
        silentRefresh = Promise.race([
            msalInstance.acquireTokenSilent({ ...loginRequest, account }),
            new Promise<never>((_resolve, reject) => {
                setTimeout(
                    () => {return reject(new Error('Silent token refresh timed out.'))},
                    SILENT_TOKEN_TIMEOUT_MS
                );
            })
        ]).finally(() => {
            silentRefresh = null;
        });
    }

    return silentRefresh;
};

const PERSISTED_STORE_IDS = [
    'UserStore',
    'DepotSchedulesStore',
    'DepotAdminProfilesStore',
    'DepotClientsStore',
    'onetwofive',
    'DepotCompanyStore',
    'DepotCompaniesStore',
    'DepotFetchProfilesStore',
    'OneTwoFiveChatbotStore',
    'DepotProfileStore',
    'DepotReviewsStore',
    'DepotFetchResponseProfilesStore',
    'DepotSectorStore',
    'DepotReviewStore'
] as const;

const STORE_IDS_TO_SKIP_RESET = new Set(['app']);

const resetActiveStores = () => {
    const pinia = getActivePinia() as
        | (ReturnType<typeof getActivePinia> & { _s?: Map<string, StoreGeneric> })
        | undefined;

    if (!pinia?._s) {
        return;
    }

    for (const store of pinia._s.values()) {
        if (STORE_IDS_TO_SKIP_RESET.has(store.$id)) {
            continue;
        }

        if (typeof store.$reset === 'function') {
            store.$reset();
            continue;
        }

        const reset = (store as StoreGeneric & { reset?: () => void }).reset;

        if (typeof reset === 'function') {
            reset.call(store);
        }
    }
};

const clearPersistedStores = () => {
    if (typeof window === 'undefined') {
        return;
    }

    for (const storeId of PERSISTED_STORE_IDS) {
        window.localStorage.removeItem(storeId);
        window.sessionStorage.removeItem(storeId);
    }
};

export const useUserStore = defineStore('UserStore', {
    persist: true,
    state: (): UserStoreState => {return {
        user: null,
        accessToken: null,
        accessTokenExpiresOn: null,
        accessTokenClaims: null,
        roles: [],
        platformToken: null,
        platformTokenExpiresOn: null,
        platformTokenClaims: null,
        platformRoles: [],
        roleOverride: null,
        allData: {},
        avatarUrl: null,
        reauthInProgress: false
    }},
    actions: {
        reset() {
            this.user = null;
            this.accessToken = null;
            this.accessTokenExpiresOn = null;
            this.accessTokenClaims = null;
            this.roles = [];
            this.clearPlatformToken();
            this.roleOverride = null;
            this.allData = {};
            this.avatarUrl = null;
            this.reauthInProgress = false;
        },
        setAccountData(accountData: any) {
            this.user = accountData.account;
        },
        clearPlatformToken() {
            this.platformToken = null;
            this.platformTokenExpiresOn = null;
            this.platformTokenClaims = null;
            this.platformRoles = [];
        },
        /**
         * Act as a lower role, to check what that role's UI looks like. Only
         * Depot.Admin and above may set one; `effectiveRoles` independently
         * ignores anything that would not reduce privilege, so this is a UX
         * guard rather than the security one.
         */
        setRoleOverride(role: string) {
            if (maxRank(this.roles) < roleRank(ROLE_ADMIN)) {
                return;
            }

            this.roleOverride = role;
        },
        clearRoleOverride() {
            this.roleOverride = null;
        },
        setUserData(account: AccountInfo | null) {
            // A role preview belongs to the account that started it. Silent token
            // refreshes re-set the same account roughly hourly and must leave the
            // preview alone, so this compares accounts rather than clearing on
            // every auth result.
            if (this.user?.homeAccountId !== account?.homeAccountId) {
                this.roleOverride = null;
                // The platform token belongs to the account that acquired it, and
                // carries that account's 125 roles. Same reasoning as the override:
                // compared by account so a routine refresh does not clear it.
                this.clearPlatformToken();
            }

            this.user = account;
        },
        async logout() {
            logAuthInfo('logout');
            useNotificationsStore().dismissByKey(AUTH_NOTIFICATION_KEY);
            useNotificationsStore().dismissByKey(PLATFORM_NOTIFICATION_KEY);
            resetActiveStores();
            clearPersistedStores();

            try {
                await msalInstance.logoutPopup();
            } finally {
                void router.push('/login');
            }
        },
        async login(): Promise<boolean> {
            try {
                const result = await msalInstance.loginPopup(interactiveLoginRequest);

                if (result.account) {
                    msalInstance.setActiveAccount(result.account);
                }

                this.setAuthResult(result);

                if (!this.accessToken) {
                    void await this.ensureAccessToken(true);
                }

                void this.ensurePlatformToken();
                void this.fetchUserPhoto();

                const redirect =
                    (router.currentRoute.value.query.redirect as string | undefined) ?? '/';
                router.replace(redirect);
                return true;
            } catch (err) {
                // A user closing the popup is expected — don't treat it as an error.
                if (isUserCancellation(err)) {
                    logAuthInfo('login_cancelled');
                    return false;
                }

                logAuthFailure('login_failed', {}, err);
                useNotificationsStore().error(
                    AUTH_MESSAGES.loginFailedTitle,
                    AUTH_MESSAGES.loginFailedBody
                );
                return false;
            }
        },
        setAuthResult(result: AuthenticationResult) {
            if (result.account) {
                this.setUserData(result.account);
            }

            this.accessToken = result.accessToken ?? null;
            this.accessTokenExpiresOn = result.expiresOn ? result.expiresOn.getTime() : null;
            this.accessTokenClaims = decodeJwt(this.accessToken);
            this.roles = getRolesFromClaims(this.accessTokenClaims);

            // A fresh, valid token means any outstanding "session expired" prompt is stale.
            if (this.accessToken) {
                useNotificationsStore().dismissByKey(AUTH_NOTIFICATION_KEY);
            }
        },
        async ensureLoginState(event: any) {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const payload = event.payload as any;
    
                this.setAuthResult(payload);
                
                const account = payload.account;
                msalInstance.setActiveAccount(account);
            }
        },
         async bootstrapFromMsal() {
            // Clear any re-auth flag that may have been persisted mid-flow before a reload.
            this.reauthInProgress = false;

            const accounts = msalInstance.getAllAccounts();
            const account = msalInstance.getActiveAccount() ?? accounts[0] ?? null;

            if (!account) {
                this.$reset();
                return;
            }

            msalInstance.setActiveAccount(account);
            this.setUserData(account);

            void await this.ensureAccessToken(false);

            // Not awaited, for the same reason the router guard does not await the
            // depot refresh: a slow platform request must not delay the first paint.
            // Until it lands, 125 roles are unknown and 125 gates go unenforced.
            void this.ensurePlatformToken();
            void this.fetchUserPhoto();
        },
        async ensureAccessToken(force: boolean = false): Promise<string | null> {
            const account = msalInstance.getActiveAccount();

            if (!account) {
                this.accessToken = null;
                this.accessTokenExpiresOn = null;
                this.accessTokenClaims = null;
                this.roles = [];
                return null;
            }

            let tokenStillValid = false;
            if (this.accessToken && this.accessTokenExpiresOn) {
                // Check if token is still valid with some skew
                tokenStillValid = Date.now() < this.accessTokenExpiresOn - EXPIRY_SKEW_MS;
            }

            if (!force && tokenStillValid) {
                return this.accessToken;
            }

            try {
                const result = await acquireTokenSilentOnce(account);

                this.setAuthResult(result);
                return this.accessToken;
            } catch (err) {
                // Silent acquisition failed — clear the now-unusable token state.
                this.accessToken = null;
                this.accessTokenExpiresOn = null;
                this.accessTokenClaims = null;
                this.roles = [];

                // An active account existed but the token could not be refreshed:
                // the session is expired/revoked or consent/MFA is required.
                if (isInteractionRequired(err)) {
                    logAuthWarning('interaction_required', { correlationId: account.homeAccountId }, err);
                    this.requireReauthentication();
                } else {
                    // Likely transient (network/server). Surface it, but don't force a
                    // re-login prompt that would be wrong while simply offline.
                    logAuthFailure('silent_token_failed', {}, err);
                }

                return null;
            }
        },
        /**
         * Acquires the 125 platform access token. Modelled on `ensureGraphToken`
         * below rather than on `ensureAccessToken` above: it runs its own silent
         * request and never calls `setAuthResult`, which would overwrite the depot
         * token and the depot roles with a token for the wrong resource.
         *
         * Where it differs from the Graph token is the failure. Graph failing costs
         * an avatar, so that one stays silent; the depot token failing means a dead
         * session, so that one raises the re-auth prompt. This failing costs the 125
         * section and nothing else — the depot half of the app is still fully usable
         * — so it notifies and NEVER calls `requireReauthentication`, because "your
         * session has expired" would simply be untrue.
         */
        async ensurePlatformToken(force: boolean = false): Promise<string | null> {
            // No configured resource is a deployment that does not use the 125 API,
            // not a user without access. Do nothing and say nothing.
            if (!platformRequest) {
                return null;
            }

            const account = msalInstance.getActiveAccount();

            if (!account) {
                this.clearPlatformToken();
                return null;
            }

            if (!force && this.platformToken && this.platformTokenExpiresOn) {
                if (Date.now() < this.platformTokenExpiresOn - EXPIRY_SKEW_MS) {
                    return this.platformToken;
                }
            }

            try {
                const result = await msalInstance.acquireTokenSilent({
                    ...platformRequest,
                    account
                });

                this.platformToken = result.accessToken ?? null;
                this.platformTokenExpiresOn = result.expiresOn
                    ? result.expiresOn.getTime()
                    : null;
                this.platformTokenClaims = decodeJwt(this.platformToken);
                this.platformRoles = getRolesFromClaims(this.platformTokenClaims);

                // A successful acquisition makes any outstanding "125 data unavailable"
                // warning untrue, and that notification is sticky, so it would otherwise
                // sit there contradicting a working section.
                if (this.platformToken) {
                    useNotificationsStore().dismissByKey(PLATFORM_NOTIFICATION_KEY);
                }

                return this.platformToken;
            } catch (err) {
                this.clearPlatformToken();
                logAuthWarning('silent_token_failed', { scope: '125-platform' }, err);

                useNotificationsStore().notify({
                    type: 'warning',
                    title: AUTH_MESSAGES.platformUnavailableTitle,
                    message: AUTH_MESSAGES.platformUnavailableBody,
                    dedupeKey: PLATFORM_NOTIFICATION_KEY
                });

                return null;
            }
        },
        async ensureGraphToken(): Promise<string | null> {
            const account = msalInstance.getActiveAccount();

            if (!account) {
                return null;
            }

            try {
                const result = await msalInstance.acquireTokenSilent({
                    ...graphRequest,
                    account
                });

                return result.accessToken ?? null;
            } catch (err) {
                // The profile photo is optional — a Graph consent/interaction failure
                // must NOT trigger a global re-auth prompt. Log it and fall back to initials.
                logAuthWarning('silent_token_failed', { scope: 'graph' }, err);
                return null;
            }
        },
        async fetchUserPhoto(): Promise<void> {
            const token = await this.ensureGraphToken();

            if (!token) {
                return;
            }

            try {
                const photo = await callMsGraphPhoto(token);

                if (photo) {
                    this.avatarUrl = photo;
                }
            } catch (err) {
                // Ignore – the avatar gracefully falls back to user initials.
            }
        },
        /**
         * Single entry point for "the session is no longer usable". Surfaces ONE
         * sticky, deduped notification with a clear call-to-action, so failures can
         * never pile up silently in the background. Safe to call repeatedly.
         */
        requireReauthentication(): void {
            // Never prompt on the login screen itself — that would be redundant
            // and risks a confusing loop.
            if (router.currentRoute.value.name === ROUTE_NAMES.LOGIN) {
                return;
            }

            const notifications = useNotificationsStore();
            notifications.notify({
                type: 'warning',
                title: AUTH_MESSAGES.sessionExpiredTitle,
                message: AUTH_MESSAGES.sessionExpiredBody,
                dedupeKey: AUTH_NOTIFICATION_KEY,
                durationMs: null,
                action: {
                    label: AUTH_MESSAGES.signInAgainCta,
                    handler: async () => {
                        await this.reauthenticate();
                    }
                }
            });
        },
        /**
         * Drives the interactive re-authentication flow. Tries a silent refresh
         * first; if interaction is genuinely required, opens the MSAL popup so the
         * user stays on the current page and can continue working afterwards.
         */
        async reauthenticate(): Promise<boolean> {
            if (this.reauthInProgress) {
                return false;
            }

            this.reauthInProgress = true;
            logAuthInfo('reauth_started');
            const notifications = useNotificationsStore();

            try {
                // Best case: the refresh token is still good and no UI is needed.
                const account = msalInstance.getActiveAccount();
                if (account) {
                    try {
                        const silent = await msalInstance.acquireTokenSilent({
                            ...loginRequest,
                            account,
                            forceRefresh: true
                        });
                        this.setAuthResult(silent);
                    } catch {
                        // Fall through to interactive login below.
                    }
                }

                if (!this.accessToken) {
                    const result = await msalInstance.loginPopup(interactiveLoginRequest);
                    if (result.account) {
                        msalInstance.setActiveAccount(result.account);
                    }

                    this.setAuthResult(result);
                }

                if (!this.accessToken) {
                    throw new Error('Re-authentication completed without an access token.');
                }

                notifications.dismissByKey(AUTH_NOTIFICATION_KEY);
                notifications.success(
                    AUTH_MESSAGES.reauthSuccessTitle,
                    AUTH_MESSAGES.reauthSuccessBody
                );
                logAuthInfo('reauth_succeeded');
                void this.ensurePlatformToken(true);
                void this.fetchUserPhoto();
                return true;
            } catch (err) {
                if (isUserCancellation(err)) {
                    // User dismissed the popup — keep the sticky prompt so they can retry.
                    logAuthInfo('login_cancelled');
                    return false;
                }

                logAuthFailure('reauth_failed', {}, err);
                // Re-raise (deduped) so the prompt stays visible with the CTA.
                notifications.notify({
                    type: 'error',
                    title: AUTH_MESSAGES.reauthFailedTitle,
                    message: AUTH_MESSAGES.reauthFailedBody,
                    dedupeKey: AUTH_NOTIFICATION_KEY,
                    durationMs: null,
                    action: {
                        label: AUTH_MESSAGES.signInAgainCta,
                        handler: async () => {
                            await this.reauthenticate();
                        }
                    }
                });
                return false;
            } finally {
                this.reauthInProgress = false;
            }
        },
    },
    getters: {
        loggedIn: (state) => {
            return !!state.accessToken;
        },
        /**
         * Rank from the token's own roles, ignoring any preview override. Gates
         * the preview control itself: gate that on `rank` instead and the control
         * disappears the moment it is used, taking the way out with it.
         */
        realRank: (state) => {
            return maxRank(state.roles);
        },
        /** Highest role rank the user holds (Staff.* > Admin > User). */
        rank: (state) => {
            return maxRank(effectiveRoles(state.roles, state.roleOverride));
        },
        /**
         * True when the user satisfies `role`, judged within `role`'s own ladder.
         *
         * Every role the user holds is handed over — depot effective roles plus 125
         * roles — because `satisfies` ignores the ones outside the named role's
         * ladder. That is what keeps a `Depot.Staff.Admin` from opening a
         * `125.Admin` route and a `125.Admin` from opening a `Depot.Admin` one,
         * and it means the six callers of this getter need no idea which product a
         * `minRole` belongs to.
         */
        canAtLeast: (state) => {return (role: string) => {
            return satisfies(
                [...effectiveRoles(state.roles, state.roleOverride), ...state.platformRoles],
                role
            );
        }},
        /**
         * True when the roles a `minRole` would be judged against are known.
         *
         * False only while the token for that role's ladder has not been acquired:
         * the roles are then unknown rather than insufficient, so the guard skips
         * the check instead of bouncing the user to Forbidden over a token still in
         * flight. A role name in no ladder reports true, so that the gate which
         * follows can refuse it — reporting false there would skip the check and
         * open the route to everyone.
         */
        rolesKnownFor: (state) => {return (role: string) => {
            const ladder = ladderNameFor(role);

            if (ladder === 'platform') {
                return !!state.platformToken;
            }

            if (ladder === 'depot') {
                return !!state.accessToken;
            }

            return true;
        }},
        /** Convenience for in-page gating of admin-only actions. */
        isAdmin: (state) => {
            return maxRank(effectiveRoles(state.roles, state.roleOverride)) >= roleRank(ROLE_ADMIN);
        },
        /** Convenience for in-page gating to internal staff (Staff.User and above). */
        isStaff: (state) => {
            return maxRank(effectiveRoles(state.roles, state.roleOverride)) >= roleRank(ROLE_STAFF_USER);
        },
        /**
         * True while a role preview is actually taking effect. Identity holds
         * because `effectiveRoles` returns `state.roles` itself whenever the
         * override is absent, unrecognized, or not a reduction.
         */
        isPreviewingRole: (state) => {
            return effectiveRoles(state.roles, state.roleOverride) !== state.roles;
        },
        /** The roles every UI gate resolves against, override applied. */
        effectiveRoles: (state) => {
            return effectiveRoles(state.roles, state.roleOverride);
        }
    }
});
